import { useCallback, useState } from "react";

export const useKnowledgeUpload = (
    fileLimit: number,
    maxFileSizeMB: number,
    openErrorDialog: (msg: string) => void,
    existingFileCount: number = 0 // Add this parameter with default 0
) => {
    const [files, setFiles] = useState<File[]>([]);

    const addFiles = useCallback(
        (incoming: File[]) => {
            setFiles(prev => {
                const existing = new Set(prev.map(f => f.name));
                const valid: File[] = [];

                // Include existing files from server in the count
                let currentTotal = prev.length + existingFileCount;

                incoming.forEach(file => {
                    if (currentTotal >= fileLimit) {
                        openErrorDialog(`File limit reached. Maximum ${fileLimit} files allowed (${currentTotal}/${fileLimit})`);
                        return;
                    }

                    const fileName = file.name.toLowerCase();
                    const isValidType = fileName.endsWith(".pdf") || fileName.endsWith(".txt") || fileName.endsWith(".json") || fileName.endsWith(".docx");

                    if (!isValidType) {
                        openErrorDialog(`${file.name}: File must be PDF, TXT, JSON, or DOCX`);
                        return;
                    }
                    if (file.size > maxFileSizeMB * 1024 * 1024) {
                        openErrorDialog(`${file.name}: File exceeds ${maxFileSizeMB} MB size limit`);
                        return;
                    }
                    if (existing.has(file.name)) {
                        return;
                    }

                    valid.push(file);
                    currentTotal++;
                });

                return [...prev, ...valid];
            });
        },
        [fileLimit, maxFileSizeMB, openErrorDialog, existingFileCount]
    );

    const removeFile = useCallback((index: number) => setFiles(p => p.filter((_, i) => i !== index)), []);

    return { files, addFiles, removeFile };
};
